import {
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useDownload } from "../../../../../../hooks/useDownload";
import { CorrectionEditBillTableProps } from "../types/CorrectionBillEdit.types";
import { SummarizedValidityIndicator } from "../../../../Base/components/SummarizedValidityIndicator";

import { MoreVert } from "@mui/icons-material";
import "../../../DirectBilling/common/helpers/css/direct-bill-info.css";
import RubberStamp from "../../../DirectBilling/common/components/RubberStamp";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import { therapyRxTermLabel } from "../../../../../../helpers/therapyRxLabel";

export const CorrectionEditBillTable: React.FC<CorrectionEditBillTableProps> = ({
  rxs,
  selectedRxId,
  onRowClick,
  onMenuOpen,
  onTaxierenClick,
  previousBillNumber,
  previousBillPdf,
  previousPaperReceipt,
}) => {
  const INSURANCE_ORDER = ["10000", "30000", "50000"];

  const sortedRxs = [...rxs].sort((a, b) => {
    const statusA = a.insuranceStatus || "";
    const statusB = b.insuranceStatus || "";

    //Compare by insurance status order
    const indexA = INSURANCE_ORDER.indexOf(statusA);
    const indexB = INSURANCE_ORDER.indexOf(statusB);
    if (indexA !== indexB) {
      return indexA - indexB;
    }

    // sort by name
    const lastNameA = (a.patient as { lastName?: string })?.lastName?.toLowerCase() || "";
    const lastNameB = (b.patient as { lastName?: string })?.lastName?.toLowerCase() || "";
    if (lastNameA !== lastNameB) {
      return lastNameA.localeCompare(lastNameB);
    }

    //  final sort using rxNumber
    const rxA = (a.rxNumber || "").toString();
    const rxB = (b.rxNumber || "").toString();
    return rxA.localeCompare(rxB);
  });

  const { handleDownloadPdf } = useDownload();

  return (
    <TableContainer
      component={Paper}
      className="table-container"
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className="table-header-cell">Name</TableCell>
            <TableCell className="table-header-cell">Rx Details</TableCell>
            <TableCell className="table-header-cell">Validierung</TableCell>
            <TableCell className="table-header-cell">Referenzrechnung</TableCell>
            <TableCell className="table-header-cell">Taxieren</TableCell>
            <TableCell className="table-header-cell"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRxs.map((row, index) => {
            const isSelected = row.id === selectedRxId;
            return (
              <TableRow
                key={index}
                className={`table-row ${isSelected ? "table-row-selected" : ""}`}
                onClick={() => {
                  onRowClick(row);
                }}
              >
                <TableCell className="table-cell">
                  <Typography>
                    {(row.patient?.lastName || "") + ", " + (row.patient?.firstName || "")}
                  </Typography>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="rx-box">
                    <Grid
                      container
                      spacing={1}
                    >
                      <Grid
                        item
                        xs={3}
                      >
                        <Typography>{row.rxNumber}</Typography>
                      </Grid>
                      <Grid
                        item
                        xs={4}
                        sx={{ textAlign: "right" }}
                      >
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          {new Date(row.date).toLocaleDateString("de-DE", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={5}
                        sx={{ textAlign: "right" }}
                      >
                        {row?.therapyRxs?.map((therapyRx: any, i: any) => (
                          <Box
                            key={i}
                            className="therapy-tag"
                          >
                            {therapyRxTermLabel(therapyRx)}
                          </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  className="table-cell"
                >
                  {SummarizedValidityIndicator(row.formvalidation)}
                </TableCell>
                {previousBillNumber && previousBillPdf && previousPaperReceipt ? (
                  <TableCell
                    align="center"
                    className="table-cell"
                  >
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                    >
                      <Grid
                        item
                        xs={0}
                      >
                        <Typography>{previousBillNumber}</Typography>
                      </Grid>
                      <Grid
                        item
                        xs={0}
                      >
                        <IconButton onClick={() => handleDownloadPdf(previousBillPdf!)}>
                          <RequestQuoteIcon />
                        </IconButton>
                      </Grid>
                      <Grid
                        item
                        xs={0}
                      >
                        <IconButton onClick={() => handleDownloadPdf(previousPaperReceipt!)}>
                          <HistoryEduIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </TableCell>
                ) : (
                  <TableCell
                    align="center"
                    className="table-cell"
                  >
                    <Typography>-</Typography>
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  className="table-cell"
                >
                  <RubberStamp
                    text={row.isTaxieren ? "Taxiert" : "Taxieren"}
                    color={row.isTaxieren ? "success" : "info"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRowClick(row);
                      onTaxierenClick(row);
                    }}
                  />
                </TableCell>
                <TableCell
                  align="center"
                  className="table-cell"
                >
                  <IconButton onClick={(event) => onMenuOpen(event, row)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
